import React from 'react';
import styled from 'styled-components';
import WalletButtons from '../../../common/WalletButtons';

const PageShell = styled.div`
  width: 100%;
  background: linear-gradient(180deg, ${props => props.theme.colors.offWhite} 0%, #ffffff 45%);
  border-radius: 20px;
  padding: clamp(1.5rem, 4vw, 2.75rem);
  border: 1px solid rgba(16, 29, 64, 0.06);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 24px 48px rgba(16, 29, 64, 0.06);

  @media print {
    display: none !important;
  }
`;

const Inner = styled.div`
  max-width: 880px;
  margin: 0 auto;
`;

const TopAccent = styled.div`
  height: 4px;
  width: 100%;
  max-width: 120px;
  margin: 0 auto 1.75rem;
  border-radius: 999px;
  background: ${props => props.$safari
    ? 'linear-gradient(90deg, #C8861A, #A83E18)'
    : `linear-gradient(90deg, ${props.theme.colors.accent}, ${props.theme.colors.primary})`};
`;

const SafariStrip = styled.div`
  background: #0C2C1B;
  border-radius: 14px;
  padding: 1.35rem 1.5rem;
  margin-bottom: 1.75rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem 1.5rem;
`;

const SafariStripLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const SafariStripKicker = styled.p`
  margin: 0 0 0.3rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #C8861A;
`;

const SafariStripTitle = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.2vw, 1.25rem);
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
`;

const SafariChecks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem 1rem;
  flex-shrink: 0;
`;

const SafariCheck = styled.span`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.78);
  display: flex;
  align-items: center;
  gap: 0.35rem;

  &::before {
    content: '✓';
    color: #C8861A;
    font-weight: 700;
    font-size: 0.72rem;
  }
`;

const Hero = styled.header`
  text-align: center;
  margin-bottom: 2rem;
`;

const StatusMark = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 1.25rem;
  border-radius: 50%;
  background: ${props => props.theme.colors.white};
  border: 2px solid rgba(157, 109, 73, 0.35);
  color: ${props => props.theme.colors.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(16, 29, 64, 0.08);

  svg {
    width: 26px;
    height: 26px;
    stroke-width: 2.5;
  }

  @media (max-width: 576px) {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;

    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

const Eyebrow = styled.span`
  display: block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
  margin-bottom: 0.5rem;
`;

const Headline = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const Lead = styled.p`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 1.0625rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.65;
  margin: 0 auto;
  max-width: 34rem;
`;

const EmailHighlight = styled.span`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const ReferenceCard = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.75rem;
  text-align: center;
  box-shadow: 0 2px 12px rgba(16, 29, 64, 0.04);
`;

const RefLabel = styled.div`
  font-family: system-ui, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.5);
  margin-bottom: 0.5rem;
`;

const RefCode = styled.div`
  font-family: ui-monospace, 'SF Mono', 'Cascadia Code', Menlo, monospace;
  font-size: clamp(1.125rem, 2.5vw, 1.375rem);
  font-weight: 600;
  letter-spacing: 0.06em;
  color: ${props => props.theme.colors.primary};
  word-break: break-all;
`;

const FamilyBanner = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1px solid rgba(157, 109, 73, 0.25);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.75rem;
  text-align: center;
  font-family: system-ui, sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const FamilyQuote = styled.figure`
  margin: 0 0 1.75rem;
  padding: 1.25rem 1.35rem;
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  border-left: 3px solid ${props => props.theme.colors.accent};
  box-shadow: 0 2px 12px rgba(16, 29, 64, 0.05);
  text-align: left;
`;

const QuoteBody = styled.blockquote`
  margin: 0;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.0625rem;
  font-style: italic;
  line-height: 1.65;
  color: ${props => props.theme.colors.secondary};
`;

const SavingsPanel = styled.section`
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border-radius: 16px;
  padding: 1.5rem 1.5rem 1.65rem;
  margin-bottom: 1.75rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(16, 29, 64, 0.22);

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 55%;
    height: 100%;
    background: radial-gradient(
      ellipse at top right,
      rgba(157, 109, 73, 0.35) 0%,
      transparent 65%
    );
    pointer-events: none;
  }
`;

const SavingsInner = styled.div`
  position: relative;
  z-index: 1;
`;

const SavingsPill = styled.span`
  display: inline-block;
  font-family: system-ui, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 0.85rem;
`;

const SavingsTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.125rem, 2.5vw, 1.35rem);
  font-weight: 600;
  margin: 0 0 1.25rem;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.96);
`;

const SavingsMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const SavingsMetric = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 1rem 1rem 0.9rem;
  text-align: center;
`;

const MetricLabel = styled.span`
  display: block;
  font-family: system-ui, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 0.45rem;
`;

const MetricValue = styled.span`
  display: block;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.35rem, 3.5vw, 1.65rem);
  font-weight: 600;
  color: ${props => props.theme.colors.white};
  letter-spacing: -0.02em;
`;

const MetricValueAccent = styled(MetricValue)`
  color: #8fd9a8;
`;

const SavingsCaption = styled.p`
  font-family: system-ui, sans-serif;
  font-size: 0.9375rem;
  margin: 1.15rem 0 0;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: ${props => props.theme.colors.white};
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 14px;
  padding: 1.35rem 1.35rem 1.25rem;
  box-shadow: 0 2px 12px rgba(16, 29, 64, 0.04);
`;

const CardTitle = styled.h3`
  font-family: system-ui, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
  margin: 0 0 1rem;
`;

const DetailRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const DL = styled.span`
  font-family: system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(16, 29, 64, 0.5);
`;

const DV = styled.span`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.0625rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  line-height: 1.35;
  word-break: break-word;
`;

const TicketsCard = styled(Card)`
  margin-bottom: 1rem;
`;

const TicketTable = styled.div`
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 10px;
  overflow: hidden;
`;

const TicketHead = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  padding: 0.65rem 1rem;
  background: ${props => props.theme.colors.offWhite};
  font-family: system-ui, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
`;

const TicketRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: center;
  padding: 1rem 1rem;
  border-top: 1px solid rgba(16, 29, 64, 0.06);
  background: ${props => props.theme.colors.white};

  &:first-of-type {
    border-top: none;
  }
`;

const TicketName = styled.span`
  font-family: ${props => props.theme.fonts.main};
  font-weight: 600;
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  display: block;
`;

const TicketQty = styled.span`
  font-family: system-ui, sans-serif;
  font-size: 0.8125rem;
  color: rgba(16, 29, 64, 0.45);
  margin-top: 0.2rem;
  display: block;
`;

const TicketPrice = styled.span`
  font-family: ${props => props.theme.fonts.main};
  font-weight: 600;
  font-size: 1rem;
  color: ${props => props.theme.colors.secondary};
  white-space: nowrap;
`;

const ReceiptCard = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 14px;
  padding: 1.35rem 1.5rem 1.25rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 20px rgba(16, 29, 64, 0.06);
`;

const ReceiptLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  padding: 0.5rem 0;
  font-family: system-ui, sans-serif;
  font-size: 0.9375rem;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(16, 29, 64, 0.06);
  }
`;

const ReceiptLineStrong = styled(ReceiptLine)`
  margin-top: 0.35rem;
  padding-top: 1rem;
  border-top: 2px solid rgba(157, 109, 73, 0.35);
  border-bottom: none;
  font-weight: 700;
  font-size: 1.0625rem;

  span:last-child {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.25rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const RL = styled.span`
  color: rgba(16, 29, 64, 0.55);
`;

const RV = styled.span`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const RVDiscount = styled(RV)`
  color: #1e7a4a;
`;

const WalletBlock = styled.div`
  margin-bottom: 1.25rem;
`;

const WalletLabel = styled.h3`
  font-family: system-ui, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
  text-align: center;
  margin: 0 0 0.5rem;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
`;

const HomeButton = styled.button`
  font-family: system-ui, sans-serif;
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border: 1px solid rgba(16, 29, 64, 0.25);
  border-radius: 10px;
  padding: 0.65rem 1.75rem;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(16, 29, 64, 0.04);
    border-color: rgba(16, 29, 64, 0.4);
  }
`;

const Footnote = styled.footer`
  margin-top: 0.25rem;
  padding: 1.15rem 1.25rem;
  background: rgba(248, 247, 244, 0.85);
  border-radius: 12px;
  border: 1px solid rgba(16, 29, 64, 0.06);
`;

const FootnoteText = styled.p`
  margin: 0;
  font-family: system-ui, sans-serif;
  font-size: 0.875rem;
  line-height: 1.65;
  color: ${props => props.theme.colors.secondary};
  text-align: center;
`;

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const BookingConfirmation = ({
  bookingReference,
  selectedTickets,
  selectedDate,
  selectedTimeSlot,
  customerDetails,
  totalAmount,
  venposBasket,
  bookingSummary,
  isFamilySaver = false,
  pageId
}) => {
  const isMembershipJourney = selectedTickets && selectedTickets.some(ticket => ticket.webSaleItemTypeID === 4);
  const isFlowerGardenShow = pageId === '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
  const familySaverLabel = isFlowerGardenShow ? 'Flower and Garden Show 2026' : 'Family Saver';
  const isSafariBooking = pageId === '94fbd17d-c40c-49f3-878e-d0bc415926b2' || pageId === '6d614bac-7055-4932-b62b-c960e7ff213b';
  const isSafariCastle = pageId === '94fbd17d-c40c-49f3-878e-d0bc415926b2';

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return 'N/A';
    return dateObj.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeSlot) => {
    return timeSlot?.time || timeSlot || 'Not specified';
  };

  const basketData = venposBasket?.data;

  const discounts = basketData?.discounts || bookingSummary?.discounts || [];
  const totalSavings = bookingSummary?.totalSavings || discounts.reduce((sum, discount) => sum + (discount.discountedAmount || 0), 0);
  const hasSavings = totalSavings > 0;

  const isFamilySaverDiscount = discounts.some(d => d.code && (d.code.toUpperCase().startsWith('FS') || d.code.toUpperCase().startsWith('FAMILYSAVER')));
  const isTravelZooDiscount = discounts.some(d => d.code && d.code.toUpperCase().startsWith('TZ'));

  let originalPrice;
  let finalPrice;
  if (bookingSummary?.originalPrice && bookingSummary?.finalPrice !== undefined) {
    originalPrice = bookingSummary.originalPrice;
    finalPrice = bookingSummary.finalPrice;
  } else if (basketData?.totalPrice && basketData?.finalPrice !== undefined) {
    originalPrice = basketData.totalPrice;
    finalPrice = basketData.finalPrice;
  } else {
    originalPrice = totalAmount;
    finalPrice = totalAmount;
  }

  const savingsPercentage = originalPrice > 0 ? ((totalSavings / originalPrice) * 100).toFixed(0) : 0;

  const basketGroups = basketData?.groups || [];
  const basketItems = basketGroups.flatMap(g => g.items || []);

  const ticketsToDisplay = basketItems.length > 0
    ? (() => {
        const groupedMap = new Map();

        basketItems.forEach(item => {
          const key = `${item.webSaleItemId}-${item.name}`;
          const itemQuantity = item.quantity || 1;
          const unitPrice = item.unitPrice || (item.totalPrice / itemQuantity) || item.price || 0;

          if (groupedMap.has(key)) {
            const existing = groupedMap.get(key);
            existing.quantity += itemQuantity;
            existing.totalPrice += item.totalPrice || (unitPrice * itemQuantity);
          } else {
            groupedMap.set(key, {
              webSaleItemId: item.webSaleItemId,
              name: item.name || item.description,
              quantity: itemQuantity,
              unitPrice,
              totalPrice: item.totalPrice || (unitPrice * itemQuantity)
            });
          }
        });

        return Array.from(groupedMap.values());
      })()
    : (() => {
        const groupedMap = new Map();

        selectedTickets.filter(t => t.quantity > 0).forEach(ticket => {
          const key = `${ticket.webSaleItemId}-${ticket.name}`;

          if (groupedMap.has(key)) {
            const existing = groupedMap.get(key);
            existing.quantity += ticket.quantity;
            existing.totalPrice += ticket.price * ticket.quantity;
          } else {
            groupedMap.set(key, {
              webSaleItemId: ticket.webSaleItemId,
              name: ticket.name,
              quantity: ticket.quantity,
              unitPrice: ticket.price,
              totalPrice: ticket.price * ticket.quantity
            });
          }
        });

        return Array.from(groupedMap.values());
      })();

  const hasChildTicket = ticketsToDisplay.some(t => {
    if (!(t.quantity > 0)) return false;
    const n = (t.name || t.title || '').toLowerCase();
    return n.includes('child') || n.includes('junior') || n.includes('under ');
  });

  const handleReturnHome = () => {
    window.location.href = '/';
  };

  const walletEventName = (() => {
    if (isMembershipJourney) return 'Belvoir Castle Membership';
    const names = ticketsToDisplay
      .filter(t => t.quantity > 0)
      .map(t => t.name || t.title)
      .filter(Boolean);
    if (names.length === 0) return 'Belvoir Castle Visit';
    if (names.length === 1) return names[0];
    return `${names[0]} +${names.length - 1} more`;
  })();

  const bookingDataForWallet = {
    bookingReference,
    eventName: walletEventName,
    selectedDate,
    selectedTimeSlot,
    selectedTickets: ticketsToDisplay,
    customerDetails,
    totalAmount
  };

  const handleWalletSuccess = () => {};

  const handleWalletError = () => {};

  const savingsPillText = isFamilySaverDiscount
    ? (isFlowerGardenShow ? 'Flower & Garden Show' : 'Family saver')
    : isTravelZooDiscount
      ? 'Travelzoo'
      : 'Promotional offer';

  const savingsHeadline = isFamilySaverDiscount
    ? `You saved with our ${familySaverLabel}`
    : isTravelZooDiscount
      ? 'Travelzoo offer applied'
      : 'Discount applied to your booking';

  return (
    <PageShell className="no-print">
      <Inner>
        <TopAccent aria-hidden="true" $safari={isSafariBooking} />
        <Hero>
          <StatusMark>
            <CheckIcon />
          </StatusMark>
          <Eyebrow>Booking confirmed</Eyebrow>
          <Headline>
            {isMembershipJourney
              ? 'Membership secured'
              : isSafariBooking
                ? 'Adventure booked!'
                : "You're all set"}
          </Headline>
          <Lead>
            {isMembershipJourney
              ? 'Thank you for joining Belvoir Castle. Your membership is confirmed.'
              : isSafariBooking
                ? `Your Belvoir Summer Safari ${isSafariCastle ? '(Castle Entry)' : '(Gardens & Playground)'} is confirmed — your explorers are ready for adventure.`
                : 'Thank you for booking your visit to Belvoir Castle. We look forward to welcoming you.'}{' '}
            A confirmation has been sent to <EmailHighlight>{customerDetails.email}</EmailHighlight>.
          </Lead>
        </Hero>

        <ReferenceCard>
          <RefLabel>Booking reference</RefLabel>
          <RefCode>{bookingReference}</RefCode>
        </ReferenceCard>

        {isSafariBooking && !isMembershipJourney && (
          <SafariStrip>
            <SafariStripLeft>
              <SafariStripKicker>Belvoir Summer Safari · 13 July – 1 September 2026</SafariStripKicker>
              <SafariStripTitle>Everything is included — just bring the explorers</SafariStripTitle>
            </SafariStripLeft>
            <SafariChecks>
              <SafariCheck>Safari Trail map</SafariCheck>
              <SafariCheck>Animal Park entry</SafariCheck>
              {hasChildTicket && (
                <SafariCheck>Free ice cream for children (trail completers)</SafariCheck>
              )}
              {isSafariCastle && <SafariCheck>Bertie the Bear castle trail</SafariCheck>}
            </SafariChecks>
          </SafariStrip>
        )}

        {isFamilySaver && !isMembershipJourney && (
          <>
            <FamilyBanner>Belvoir Castle — supporting families</FamilyBanner>
            <FamilyQuote>
              <QuoteBody>
                A heartfelt thank you from the Duchess and her team — we look forward to welcoming you to Belvoir Castle.
              </QuoteBody>
            </FamilyQuote>
          </>
        )}

        {hasSavings && (
          <SavingsPanel aria-labelledby="savings-heading">
            <SavingsInner>
              <SavingsPill>{savingsPillText}</SavingsPill>
              <SavingsTitle id="savings-heading">{savingsHeadline}</SavingsTitle>
              <SavingsMetrics>
                <SavingsMetric>
                  <MetricLabel>Amount saved</MetricLabel>
                  <MetricValueAccent>£{totalSavings.toFixed(2)}</MetricValueAccent>
                </SavingsMetric>
                <SavingsMetric>
                  <MetricLabel>Original price</MetricLabel>
                  <MetricValue>£{originalPrice.toFixed(2)}</MetricValue>
                </SavingsMetric>
                <SavingsMetric>
                  <MetricLabel>Amount paid</MetricLabel>
                  <MetricValue>£{finalPrice.toFixed(2)}</MetricValue>
                </SavingsMetric>
              </SavingsMetrics>
              <SavingsCaption>
                {isMembershipJourney
                  ? `You saved ${savingsPercentage}% on your membership.`
                  : `You saved ${savingsPercentage}% on your visit to Belvoir Castle.`}
              </SavingsCaption>
            </SavingsInner>
          </SavingsPanel>
        )}

        <CardGrid>
          {!isMembershipJourney ? (
            <Card>
              <CardTitle>Visit details</CardTitle>
              <DetailRows>
                <DetailRow>
                  <DL>Date</DL>
                  <DV>{formatDate(selectedDate)}</DV>
                </DetailRow>
                <DetailRow>
                  <DL>Arrival time</DL>
                  <DV>{formatTime(selectedTimeSlot)}</DV>
                </DetailRow>
                <DetailRow>
                  <DL>Tickets</DL>
                  <DV>{ticketsToDisplay.reduce((sum, ticket) => sum + ticket.quantity, 0)}</DV>
                </DetailRow>
              </DetailRows>
            </Card>
          ) : (
            <Card>
              <CardTitle>Membership</CardTitle>
              <DetailRows>
                <DetailRow>
                  <DL>Memberships</DL>
                  <DV>{ticketsToDisplay.reduce((sum, ticket) => sum + ticket.quantity, 0)}</DV>
                </DetailRow>
              </DetailRows>
            </Card>
          )}

          <Card>
            <CardTitle>Contact</CardTitle>
            <DetailRows>
              <DetailRow>
                <DL>Name</DL>
                <DV>
                  {customerDetails.firstName} {customerDetails.lastName}
                </DV>
              </DetailRow>
              <DetailRow>
                <DL>Email</DL>
                <DV>{customerDetails.email}</DV>
              </DetailRow>
              {customerDetails.phone && (
                <DetailRow>
                  <DL>Phone</DL>
                  <DV>{customerDetails.phone}</DV>
                </DetailRow>
              )}
            </DetailRows>
          </Card>
        </CardGrid>

        <TicketsCard>
          <CardTitle>{isMembershipJourney ? 'Your memberships' : 'Tickets'}</CardTitle>
          <TicketTable>
            <TicketHead>
              <span>Item</span>
              <span>Price</span>
            </TicketHead>
            {ticketsToDisplay.map((ticket, index) => {
              const displayPrice = ticket.totalPrice || 0;
              return (
                <TicketRow key={`${ticket.webSaleItemId}-${index}`}>
                  <div>
                    <TicketName>{ticket.name}</TicketName>
                    <TicketQty>Quantity ×{ticket.quantity}</TicketQty>
                  </div>
                  <TicketPrice>£{displayPrice.toFixed(2)}</TicketPrice>
                </TicketRow>
              );
            })}
          </TicketTable>
        </TicketsCard>

        <ReceiptCard>
          {hasSavings ? (
            <>
              <ReceiptLine>
                <RL>Original price</RL>
                <RV>£{originalPrice.toFixed(2)}</RV>
              </ReceiptLine>
              {discounts.map((discount, index) => {
                const code = discount.code ? discount.code.toUpperCase() : '';
                let discountLabel;

                if (isMembershipJourney) {
                  discountLabel = 'Discount applied';
                } else if (code.startsWith('FS') || code.startsWith('FAMILYSAVER')) {
                  discountLabel = 'Family saver';
                } else if (code.startsWith('TZ')) {
                  discountLabel = 'Travelzoo';
                } else {
                  discountLabel = `${discount.code}`;
                }

                return (
                  <ReceiptLine key={index}>
                    <RL>{discountLabel}</RL>
                    <RVDiscount>-£{(discount.discountedAmount || 0).toFixed(2)}</RVDiscount>
                  </ReceiptLine>
                );
              })}
              <ReceiptLineStrong>
                <RL>Total paid (inc. VAT)</RL>
                <RV>£{finalPrice.toFixed(2)}</RV>
              </ReceiptLineStrong>
            </>
          ) : (
            <ReceiptLineStrong>
              <RL>Total paid (inc. VAT)</RL>
              <RV>£{finalPrice.toFixed(2)}</RV>
            </ReceiptLineStrong>
          )}
        </ReceiptCard>

        <WalletBlock>
          {!isMembershipJourney && (
            <>
              <WalletLabel>Add to your phone</WalletLabel>
              <WalletButtons
                bookingData={bookingDataForWallet}
                selectedTickets={ticketsToDisplay}
                selectedDate={selectedDate}
                selectedTimeSlot={selectedTimeSlot}
                customerDetails={customerDetails}
                totalAmount={totalAmount}
                venposBasket={venposBasket}
                bookingSummary={bookingSummary}
                pageId={pageId}
                isFamilySaver={isFamilySaver}
                onSuccess={handleWalletSuccess}
                onError={handleWalletError}
              />
            </>
          )}
        </WalletBlock>

        <Actions>
          <HomeButton type="button" onClick={handleReturnHome}>
            Return home
          </HomeButton>
        </Actions>

        <Footnote>
          <FootnoteText>
            {isMembershipJourney
              ? 'Please check your email for membership confirmation and further details. Your membership information will follow separately.'
              : 'Check your inbox for your confirmation and ticket details. Use Add to Apple Wallet or Google Wallet above for quick access on the day of your visit.'}
          </FootnoteText>
        </Footnote>
      </Inner>
    </PageShell>
  );
};

export default BookingConfirmation;
